import { CtxMapV2, MapV2MobileModalSize } from 'components/mapV2/contexts/CtxMapV2';
import { MapV2GlassEffectBackground } from 'components/mapV2/elements/MapV2GlassEffectBackground';
import { useContext, useEffect, useRef, useState } from 'react';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';
import styled from 'styled-components/macro';
import { Drawer } from 'vaul';

interface Props {
    className?: string;
    children: React.ReactNode;
    onMaxSnapSizeChanged: (height: string) => void;
}

export const MapV2ModalLayoutMobile = (props: Props): JSX.Element => {
    const ctxModal = useContext(CtxModal);
    const ctxView = useContext(CtxView);
    const ctxMapV2 = useContext(CtxMapV2);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const draggableRef = useRef(false);

    const isDetailOpen = !!ctxModal.detailModal?.detail;
    const lastSnapSizeList = Math.floor(windowHeight - 80 - (ctxMapV2.isIframe ? 35 : 0));
    const lastSnapSizeDetail = Math.floor(windowHeight - 20 - (ctxMapV2.isIframe ? 35 : 0));
    const lastSnapSize = isDetailOpen ? lastSnapSizeDetail : lastSnapSizeList;

    const firstSnap = ctxMapV2.features.isListEnabled ? 104 : 0;

    const snaps = [firstSnap, lastSnapSize];
    const snapState = ctxMapV2.mobileModalState.size;
    const setSnapState = ctxMapV2.mobileModalState.setSize;

    function snapStateToSnapSize(snapState: MapV2MobileModalSize): number {
        switch (snapState) {
            case 'small':
                return snaps[0];
            case 'large':
                return snaps[1];
        }
    }

    const snapSize = snapStateToSnapSize(snapState);

    // 30px is the top padding of the drawer
    useEffect(() => {
        props.onMaxSnapSizeChanged(`${lastSnapSize - 30}px`);
    }, [snapState, ctxMapV2.fullscreen.isFullscreen, windowHeight]);

    useEffect(() => {
        function resizeHandler() {
            setWindowHeight(window.innerHeight);
            ctxView.mapRef?.current?.resize();
        }
        window.addEventListener('resize', resizeHandler);
        resizeHandler();
        return () => window.removeEventListener('resize', resizeHandler);
    }, []);

    useEffect(() => {
        function messageHandler(message: MessageEvent) {
            const data = message?.data;
            if (!data) {
                return;
            }

            if (data?.type === 'resize') {
                setWindowHeight(window.innerHeight);
                ctxView.mapRef?.current?.resize();
            }
        }

        window.addEventListener('message', messageHandler);
        return () => window.removeEventListener('message', messageHandler);
    }, [ctxView.mapRef, ctxMapV2.mobileModalState]);

    useEffect(() => {
        setWindowHeight(window.innerHeight);
    }, [window.innerHeight]);

    useEffect(() => {
        if (ctxMapV2.search.value) {
            setSnapState('large');
        }
    }, [ctxMapV2.search.value]);

    useEffect(() => {
        if (snapSize === lastSnapSizeList || snapSize === lastSnapSizeDetail) {
            setSnapState('large');
        }
    }, [isDetailOpen]);

    useEffect(() => {
        if (snapState === 'small') {
            //close android and apple keyboard
            if (document?.activeElement) {
                (document?.activeElement as HTMLElement)?.blur();
            }
        }
    }, [snapState]);

    return (
        <S_MapV2ModalLayoutMobile className={props.className}>
            <Drawer.Root
                noBodyStyles
                scrollLockTimeout={500}
                handleOnly={snapState === 'large'}
                dismissible={false}
                onDrag={() => {
                    draggableRef.current = true;
                }}
                onRelease={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setTimeout(() => {
                        draggableRef.current = false;
                    }, 500);
                }}
                snapPoints={snaps.map((s) => `${s}px`)}
                activeSnapPoint={`${snapSize}px`}
                open={true}
                setActiveSnapPoint={(snap) => {
                    if (snap) {
                        const size = parseInt(String(snap).replace('px', ''));
                        const index = snaps.indexOf(size);
                        if (index === 0) {
                            ctxMapV2.mobileModalState.setSize('small');
                        }
                        if (index === 1) {
                            ctxMapV2.mobileModalState.setSize('large');
                        }
                    }
                }}
                direction="bottom"
            >
                <S_DrawerPortal>
                    <S_DrawerContent>
                        <MapV2GlassEffectBackground color={'#f6f7f5'} />
                        <S_DrawerHandle
                            onDoubleClick={(e) => {
                                e.preventDefault();
                            }}
                            onClick={() => {
                                if (draggableRef.current) {
                                    return;
                                }
                                if (snapState === 'small') {
                                    setSnapState('large');
                                } else {
                                    setSnapState('small');
                                }
                            }}
                        >
                            <S_Puller
                                $imageUrl={
                                    snapState === 'small'
                                        ? 'https://dvtqxks0zalev.cloudfront.net/07c60408-5a7e-4c80-b990-68a75b89b0d9.png'
                                        : 'https://dvtqxks0zalev.cloudfront.net/4ea115d1-87f6-45b4-9954-f12c6c1fd680.png'
                                }
                            />
                            {isDetailOpen && snapState === 'large' && (
                                <S_PullerArea
                                    onClick={(e) => {
                                        // So the click doesn't do anything
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                />
                            )}
                        </S_DrawerHandle>
                        {props.children}
                    </S_DrawerContent>
                </S_DrawerPortal>
            </Drawer.Root>
        </S_MapV2ModalLayoutMobile>
    );
};

const S_MapV2ModalLayoutMobile = styled.div`
    position: absolute;
    bottom: 0;
`;

const S_Puller = styled.div<{ $imageUrl: string }>`
    position: absolute;
    width: 18px;
    height: 18px;
    top: 14px;
    left: calc(50% - 10px);
    pointer-events: none;
    background-image: url(${(props) => props.$imageUrl});
    background-size: contain;
    background-repeat: no-repeat;
`;

const S_PullerArea = styled.div`
    position: absolute;
    top: 25px;
    left: 130px;
    height: 51px;
    right: 70px;
`;

const S_DrawerPortal = styled(Drawer.Portal)``;

const S_DrawerContent = styled(Drawer.Content)`
    position: fixed;
    // Important is needed to ovveride styles in Drawer
    // in case when input is focused, and then fullscreen
    // is turned off, it would break the layout
    bottom: 0 !important;
    height: 100% !important;
    left: 0;
    right: 0;
    z-index: 11;
    padding-top: 30px;
    outline: none;
    border-radius: 10px 10px 0 0;
`;

const S_DrawerHandle = styled(Drawer.Handle)`
    position: absolute;
    top: 0;
    height: 42px;
    left: 60px;
    right: 60px;
    width: auto;
    opacity: 1;
    z-index: 1;
`;
